import axios, { AxiosInstance } from 'axios'
import { getCurrentApiActions } from '../constants'
import { getOmniPreviewToken } from '../utils/get-omni-preview-token'
import { handleOmniResponsePreview } from '../scripts-injecting/draw-preview-watermark'

/**
 * POST请求参数接口
 */
export interface IPostParams<T> {
  /** 请求URL */
  url: string
  /** 请求数据 */
  data: T
}

export interface IGetParams<T> {
  /** 请求URL */
  url: string
  /** 请求参数 */
  data: T
}

/**
 * 分页层级数据选项
 */
export interface IPaginationLayerDataOption {
  perPage: number
  page: number
}

/**
 * 内容层级数据选项
 */
export interface IContentLayerDataOption {
  contentId: string
}

/**
 * 层级数据选项
 */
export type ILayerDataOption =
  | {
      layerId: string
      type: 'pagination'
      param: IPaginationLayerDataOption
    }
  | {
      layerId: string
      type: 'content'
      param: IContentLayerDataOption
    }

/**
 * 页面数据请求参数接口
 */
export interface IGetPageDataParams {
  /** 页面ID */
  pageId: string
  /** 层级数据选项 */
  layers?: ILayerDataOption[]
}

/**
 * 页面数据响应接口
 */
export interface IPageDataResponse {
  /** 层级数据 */
  layers: any[]
  /** 插件配置 */
  plugins?: any[]
  /** 页面设置 */
  setting?: any
}

/**
 * 服务类,处理HTTP请求
 */
class Service {
  private axiosInstance: AxiosInstance = axios.create({})

  constructor() {
    this.initAxiosInstance()
  }

  /**
   * 初始化axios实例
   */
  private initAxiosInstance() {
    /**
     * 创建一个axios实例用于HTTP请求
     */
    this.axiosInstance = axios.create({})
  }

  /**
   * 发送GET请求
   * @param url - 请求URL
   * @param data - 请求参数
   * @returns 请求响应数据
   * @throws 当响应状态不为0时抛出错误
   */
  private async get<T, R>({ url, data }: IGetParams<T>): Promise<R> {
    const previewToken = getOmniPreviewToken()

    let params = previewToken
      ? {
          ...(data || {}),
          omni_preview_token: previewToken,
          _fl_omin_timeStamp: +new Date(),
        }
      : data

    const res = await this.axiosInstance.get<{
      status: number
      msg: string
      data: R
    }>(url, {
      params,
    })

    if (previewToken) {
      handleOmniResponsePreview(res.data)
    }

    if (res.data.status !== 0) {
      throw res.data
    }
    return res.data.data
  }

  /**
   * 发送 GET 请求（公共方法）
   * @param url - 请求URL
   * @param data - 请求参数
   * @returns 请求响应数据
   * @throws 当响应状态不为0时抛出错误
   */
  public async omniRequestGet<T, R>({ url, data }: IGetParams<T>): Promise<R> {
    const previewToken = getOmniPreviewToken()

    let params = previewToken
      ? {
          ...(data || {}),
          omni_preview_token: previewToken,
          _fl_omin_timeStamp: +new Date(),
        }
      : data

    const res = await this.axiosInstance.get<R>(url, {
      params,
    })

    if (previewToken) {
      handleOmniResponsePreview(res.data)
    }

    return res.data
  }

  /**
   * 获取页面数据
   * @param pageId - 页面ID
   * @param layers - 层级数据选项
   * @returns {Promise<IPageDataResponse>} 包含页面数据的响应对象
   */
  getPageData({ pageId, layers }: IGetPageDataParams): Promise<IPageDataResponse> {
    const apiActions = getCurrentApiActions()
    const url = apiActions.getPageData
    return this.get<any, IPageDataResponse>({
      url,
      data: {
        param: {
          pageId,
          layers,
        },
      },
    })
  }

  /**
   * 获取层级数据
   * @param pageId - 页面ID
   * @param layerDataOptions - 层级数据选项
   * @returns {Promise<IPageDataResponse>} 包含层级数据的响应对象
   */
  getLayersData({
    pageId,
    layerDataOptions,
  }: {
    pageId: string
    layerDataOptions: ILayerDataOption[]
  }): Promise<IPageDataResponse> {
    const apiActions = getCurrentApiActions()
    const url = apiActions.getLayerData
    return this.get<any, IPageDataResponse>({
      url,
      data: {
        param: {
          pageId,
          layers: layerDataOptions,
        },
      },
    })
  }
}

/**
 * 导出服务实例
 */
export const service = new Service()
